/**
 * Voice & Consent (P5 Wave 4 §6) — read-mostly.
 *
 * Look up a member by id to see:
 *   • a voice-DNA card — fidelity, speaker cosine, a "ready" pill, watermark
 *     protection, and the last clone rebuild time;
 *   • a consent block — AB2602 (California voice-likeness) state, with expiry
 *     and revocation;
 *   • a mirror-access grants table — the invitees who can converse with this
 *     member's Mirror, each revocable.
 *
 * The ONLY mutation is revoking a mirror-access grant, gated behind
 * QuillActionConfirm with explicit production copy.
 *
 * SECURITY: mirror-access tokens are hash-stored; the backend returns metadata
 * only and this page NEVER renders a token value (there is no token field in
 * the data at all). A member with no Quill voice profile returns 404, rendered
 * as a friendly EmptyState rather than an error.
 */

import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Button,
  TextField,
  Skeleton,
  Divider,
} from '@mui/material';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import ShieldIcon from '@mui/icons-material/Shield';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useQuillHealth } from '../QuillHealthContext';
import EmptyState from '../../common/EmptyState';
import DataTable, { Column } from '../../common/DataTable';
import QuillActionConfirm from '../common/QuillActionConfirm';
import { colors, typography } from '../../../theme/designTokens';
import {
  voiceConsentApi,
  QuillApiError,
  VoiceConsentProfile,
  MirrorAccessGrant,
  ConsentState,
} from './api';

// ── Formatting helpers ─────────────────────────────────────────────────────

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** A [0,1] score → percentage string, else an em dash. */
function pct(v: unknown): string {
  const n = num(v);
  return n === null ? '—' : `${(n * 100).toFixed(1)}%`;
}

function fmtDate(v?: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

/** Derive an AB2602 consent label from the backend `consent` object. The backend
 *  returns `{present, active, revoked_at, expires_at, ...}` — not a pre-derived
 *  state string — so the page computes the display state here. */
function deriveConsentState(consent?: ConsentState | null): string {
  if (!consent || !consent.present) return 'none';
  if (consent.revoked_at) return 'revoked';
  if (consent.active) return 'active';
  // Present, un-revoked, but not active → past its expiry.
  if (consent.expires_at && new Date(consent.expires_at).getTime() <= Date.now()) return 'expired';
  return 'expired';
}

/** Consent state → chip color + label. */
function consentChip(state?: string | null): { label: string; bg: string; fg: string; border: string } {
  const s = (state || 'none').toLowerCase();
  if (s === 'active' || s === 'valid' || s === 'consented') {
    return { label: 'Active', bg: colors.success[50], fg: colors.success.dark, border: colors.success.light };
  }
  if (s === 'revoked') {
    return { label: 'Revoked', bg: colors.error[50], fg: colors.error.dark, border: colors.error.light };
  }
  if (s === 'expired') {
    return { label: 'Expired', bg: colors.warning[50], fg: colors.warning.dark, border: colors.warning.light };
  }
  return { label: 'None', bg: colors.gray[100], fg: colors.gray[600], border: colors.gray[300] };
}

// ── KV row ─────────────────────────────────────────────────────────────────

const KvRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    <Box sx={{ textAlign: 'right', fontWeight: 500 }}>{children}</Box>
  </Box>
);

// ── Voice DNA card ───────────────────────────────────────────────────────────

const VoiceDnaCard: React.FC<{ profile: VoiceConsentProfile }> = ({ profile }) => {
  const dna = profile.voice_dna;
  const ready = Boolean(dna?.ready);
  return (
    <Card variant="outlined" sx={{ borderRadius: '16px', height: '100%', borderColor: colors.gray[200] }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <GraphicEqIcon sx={{ color: colors.deepTeal[500] }} />
          <Typography sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg }}>
            Voice DNA
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Chip
            label={ready ? 'Ready' : 'Not ready'}
            size="small"
            sx={{
              fontWeight: 700,
              backgroundColor: ready ? colors.success[50] : colors.gray[100],
              color: ready ? colors.success.dark : colors.gray[600],
              border: `1px solid ${ready ? colors.success.light : colors.gray[300]}`,
            }}
          />
        </Box>

        {!dna ? (
          <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
            No voice clone has been built for this member yet.
          </Typography>
        ) : (
          <>
            <KvRow label="Fidelity score">{pct(dna.fidelity_score)}</KvRow>
            <KvRow label="Speaker cosine">{pct(dna.cosine)}</KvRow>
            <KvRow label="Model version">{dna.voice_model_version ?? '—'}</KvRow>
            {dna.build_type && <KvRow label="Build type">{dna.build_type}</KvRow>}
            <KvRow label="Watermark">
              {dna.watermark ? (
                <Chip
                  icon={<ShieldIcon sx={{ fontSize: 14 }} />}
                  label={dna.watermark}
                  size="small"
                  variant="outlined"
                  sx={{ color: colors.deepTeal[500], borderColor: colors.deepTeal[300] }}
                />
              ) : (
                <Typography component="span" variant="body2" sx={{ color: 'text.disabled' }}>
                  Not tracked
                </Typography>
              )}
            </KvRow>
            <KvRow label="Last rebuild">{fmtDate(dna.last_rebuild)}</KvRow>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ── Consent card ─────────────────────────────────────────────────────────────

const ConsentCard: React.FC<{ profile: VoiceConsentProfile }> = ({ profile }) => {
  const consent = profile.consent;
  const chip = consentChip(deriveConsentState(consent));
  return (
    <Card variant="outlined" sx={{ borderRadius: '16px', height: '100%', borderColor: colors.gray[200] }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <VerifiedUserIcon sx={{ color: colors.deepTeal[500] }} />
          <Typography sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg }}>
            Consent (AB 2602)
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Chip
            label={chip.label}
            size="small"
            sx={{ fontWeight: 700, backgroundColor: chip.bg, color: chip.fg, border: `1px solid ${chip.border}` }}
          />
        </Box>

        {!consent ? (
          <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
            No AB 2602 consent record for this member.
          </Typography>
        ) : (
          <>
            <KvRow label="Policy version">{consent.version ?? 'AB2602-v1'}</KvRow>
            <KvRow label="Acknowledged">{fmtDate(consent.acknowledged_at)}</KvRow>
            <KvRow label="Expires">{fmtDate(consent.expires_at)}</KvRow>
            <KvRow label="Revoked">{fmtDate(consent.revoked_at)}</KvRow>
            {consent.revoked_reason && <KvRow label="Revoke reason">{consent.revoked_reason}</KvRow>}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const VoiceConsentPage: React.FC = () => {
  const { status } = useQuillHealth();

  const [memberInput, setMemberInput] = useState('');
  const [activeMember, setActiveMember] = useState<string | null>(null);

  const [profile, setProfile] = useState<VoiceConsentProfile | null>(null);
  const [grants, setGrants] = useState<MirrorAccessGrant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  // Revoke dialog state.
  const [revokeTarget, setRevokeTarget] = useState<MirrorAccessGrant | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  // Last-request-wins guard for rapid re-lookups.
  const seqRef = useRef(0);

  const lookup = useCallback(async (memberId: string) => {
    const id = memberId.trim();
    if (!id) return;
    const seq = ++seqRef.current;
    setActiveMember(id);
    setLoading(true);
    setError(null);
    setNotFound(false);
    setProfile(null);
    setGrants([]);
    try {
      const prof = await voiceConsentApi.getMember(id);
      // Grants are a secondary read — an error there shouldn't blank the profile.
      let grantRows: MirrorAccessGrant[] = [];
      try {
        const res = await voiceConsentApi.getMirrorAccess(id);
        grantRows = res.grants ?? [];
      } catch {
        grantRows = [];
      }
      if (seq !== seqRef.current) return;
      setProfile(prof);
      setGrants(grantRows);
    } catch (e) {
      if (seq !== seqRef.current) return;
      if (e instanceof QuillApiError && e.status === 404) {
        setNotFound(true);
      } else {
        setError(e instanceof Error ? e.message : 'Failed to load voice & consent profile.');
      }
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, []);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    lookup(memberInput);
  };

  const openRevoke = (grant: MirrorAccessGrant) => {
    setRevokeTarget(grant);
    setRevokeReason('');
    setRevokeError(null);
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    setRevokeError(null);
    try {
      await voiceConsentApi.revokeMirrorAccess(revokeTarget.id, revokeReason.trim());
      setRevokeTarget(null);
      // Reflect the revocation by re-reading the grants for the active member.
      if (activeMember) {
        try {
          const res = await voiceConsentApi.getMirrorAccess(activeMember);
          setGrants(res.grants ?? []);
        } catch {
          // Fall back to dropping the row locally if the re-read fails.
          setGrants((prev) => prev.filter((g) => g.id !== revokeTarget.id));
        }
      }
    } catch (e) {
      setRevokeError(e instanceof Error ? e.message : 'Failed to revoke access.');
    } finally {
      setRevoking(false);
    }
  };

  const grantColumns: Column<MirrorAccessGrant>[] = [
    {
      id: 'asker_label',
      label: 'Asker',
      render: (row) => (
        <Typography sx={{ fontWeight: 600, fontSize: typography.sizes.sm }}>
          {row.asker_label || <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>Unlabeled</Box>}
        </Typography>
      ),
    },
    { id: 'expires_at', label: 'Expires', render: (row) => fmtDate(row.expires_at) },
    { id: 'last_used_at', label: 'Last used', render: (row) => fmtDate(row.last_used_at) },
    {
      id: 'actions',
      label: '',
      align: 'right',
      render: (row) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => openRevoke(row)}
          sx={{ textTransform: 'none', borderColor: colors.error.light, color: colors.error.main }}
        >
          Revoke
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            backgroundColor: `${colors.deepTeal[500]}1A`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
            flexShrink: 0,
          }}
        >
          <RecordVoiceOverIcon sx={{ fontSize: 28, color: colors.deepTeal[500] }} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: typography.fonts.display, fontSize: '1.75rem', fontWeight: 700, color: colors.gray[900] }}>
            Voice &amp; Consent
          </Typography>
          <Typography sx={{ fontFamily: typography.fonts.body, fontSize: typography.sizes.sm, color: 'text.secondary' }}>
            A member's voice-clone status, AB 2602 consent state, and active Mirror-access grants.
          </Typography>
        </Box>
      </Box>

      {/* Connection state */}
      <Typography
        variant="caption"
        sx={{ display: 'block', mb: 3, color: status === 'up' ? 'success.main' : 'text.disabled' }}
      >
        ● Quill backend • {status === 'up' ? 'connected' : status}
      </Typography>

      {/* Member lookup */}
      <Box
        component="form"
        onSubmit={onSubmit}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          flexWrap: 'wrap',
          border: `1px solid ${colors.gray[200]}`,
          borderRadius: '16px',
          p: 2,
          mb: 3,
        }}
      >
        <TextField
          size="small"
          value={memberInput}
          onChange={(e) => setMemberInput(e.target.value)}
          placeholder="member id"
          label="Member ID"
          inputProps={{ 'aria-label': 'Member ID', style: { fontFamily: typography.fonts.mono } }}
          sx={{ minWidth: 320, flexGrow: 1, maxWidth: 520 }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={loading || !memberInput.trim()}
          sx={{ textTransform: 'none', mt: 0.25 }}
        >
          {loading ? 'Looking up…' : 'Look up'}
        </Button>
      </Box>

      {/* Results */}
      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => activeMember && lookup(activeMember)} sx={{ textTransform: 'none' }}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={260} sx={{ borderRadius: '16px' }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={260} sx={{ borderRadius: '16px' }} />
          </Grid>
        </Grid>
      )}

      {!loading && notFound && (
        <EmptyState
          icon="person"
          title="No Quill voice profile for this member"
          description={`No voice-clone or consent record exists for "${activeMember}". Double-check the member id.`}
        />
      )}

      {!loading && !error && !notFound && profile && (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <VoiceDnaCard profile={profile} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ConsentCard profile={profile} />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          <Typography sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg, mb: 2 }}>
            Mirror-access grants
          </Typography>
          <DataTable<MirrorAccessGrant>
            columns={grantColumns}
            data={grants}
            emptyMessage="No active Mirror-access grants"
            emptyIcon="key"
          />
        </>
      )}

      {/* Revoke confirmation — the only mutation on this page. */}
      <QuillActionConfirm
        open={Boolean(revokeTarget)}
        title="Revoke Mirror access"
        destructive
        message={
          <>
            Revoke Mirror access for{' '}
            <strong>{revokeTarget?.asker_label || 'this unlabeled invitee'}</strong>? They will immediately lose the
            ability to converse with this member's Mirror. This hits the live Quill database.
          </>
        }
        detail={
          <TextField
            size="small"
            fullWidth
            label="Reason (optional)"
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            placeholder="Why is this grant being revoked?"
            inputProps={{ 'aria-label': 'Revoke reason' }}
          />
        }
        caption={revokeError || undefined}
        confirmLabel="Revoke access"
        pendingLabel="Revoking…"
        loading={revoking}
        onConfirm={confirmRevoke}
        onCancel={() => setRevokeTarget(null)}
      />
    </Box>
  );
};

export default VoiceConsentPage;
